import { Children, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";

/**
 * Resizable, collapsible pane layout.
 *
 * `sizes` gives one entry per child: a number is a fixed width/height in px (resizable and,
 * by default, collapsible); a string such as "1fr" is a flexible share of the remaining
 * space. Gutters between panes drag; the chevron on a gutter collapses/expands the adjacent
 * fixed pane; double-click resets. Sizes persist per `id` in localStorage.
 */
export interface SplitProps {
  id: string;
  sizes: (number | string)[];
  direction?: "row" | "column";
  minSizes?: number[];
  collapsible?: boolean[];
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

interface Track {
  size: number;
  unit: "px" | "fr";
}

interface Persisted {
  tracks: Track[];
  collapsed: boolean[];
}

const GUTTER = "var(--split-gutter, 6px)";
const DEFAULT_MIN = 96;
const KEY = (id: string) => `rs.split.${id}`;

function parseSizes(sizes: (number | string)[]): Track[] {
  return sizes.map((s) => {
    if (typeof s === "number") return { size: s, unit: "px" };
    const m = /^([\d.]+)\s*fr$/.exec(s.trim());
    return { size: m ? parseFloat(m[1]) : 1, unit: "fr" };
  });
}

function load(id: string, n: number): Persisted | null {
  try {
    const raw = localStorage.getItem(KEY(id));
    if (!raw) return null;
    const p = JSON.parse(raw) as Persisted;
    if (!Array.isArray(p.tracks) || p.tracks.length !== n) return null;
    return { tracks: p.tracks, collapsed: Array.isArray(p.collapsed) && p.collapsed.length === n ? p.collapsed : new Array(n).fill(false) };
  } catch {
    return null;
  }
}

function save(id: string, p: Persisted) {
  try {
    localStorage.setItem(KEY(id), JSON.stringify(p));
  } catch {
    /* quota / private mode */
  }
}

export function Split({ id, sizes, direction = "row", minSizes, collapsible, className, style, children }: SplitProps) {
  const kids = Children.toArray(children); // drops null / false / undefined (conditional panes)
  const n = kids.length;
  const horizontal = direction === "row";
  const defaults = useMemo(() => parseSizes(sizes.slice(0, n)), [sizes, n]);
  const ref = useRef<HTMLDivElement>(null);

  const [state, setState] = useState<Persisted>(() => load(id, n) ?? { tracks: defaults, collapsed: new Array(n).fill(false) });
  const [dragging, setDragging] = useState<number | null>(null);
  // Pointer capture retargets click events to the gutter, so chevron presses are resolved here
  // from the pointer sequence: a press that moved is a drag, a still press on a chevron toggles.
  const moved = useRef(false);
  const pressOnToggle = useRef(false);

  // child count changed (conditional pane) → re-seed from defaults / storage
  useEffect(() => {
    if (state.tracks.length !== n) setState(load(id, n) ?? { tracks: defaults, collapsed: new Array(n).fill(false) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n, id]);

  const commit = useCallback(
    (next: Persisted) => {
      setState(next);
      save(id, next);
    },
    [id],
  );

  const isCollapsible = (i: number) => (collapsible?.[i] ?? state.tracks[i]?.unit === "px") === true;
  const minOf = (i: number) => minSizes?.[i] ?? DEFAULT_MIN;

  const template = state.tracks
    .map((t, i) => {
      if (state.collapsed[i]) return "0px";
      return t.unit === "px" ? `${Math.round(t.size)}px` : `minmax(0, ${t.size}fr)`;
    })
    .flatMap((t, i) => (i < n - 1 ? [t, GUTTER] : [t]))
    .join(" ");

  // --- drag ------------------------------------------------------------------------------
  const measure = (): number[] => {
    const el = ref.current;
    if (!el) return state.tracks.map((t) => t.size);
    const out: number[] = [];
    for (let i = 0; i < n; i++) {
      const cell = el.children[i * 2] as HTMLElement | undefined;
      const r = cell?.getBoundingClientRect();
      out.push(r ? (horizontal ? r.width : r.height) : 0);
    }
    return out;
  };

  const onGutterDown = (k: number) => (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const target = e.currentTarget;
    const pressedToggle = (e.target as Element).closest?.(".split-toggle") as HTMLElement | null;
    const toggleIdx = pressedToggle?.dataset.target != null ? Number(pressedToggle.dataset.target) : null;
    pressOnToggle.current = toggleIdx != null;
    target.setPointerCapture(e.pointerId);
    const startPos = horizontal ? e.clientX : e.clientY;
    const startPx = measure();
    // neighbours that are actually visible
    let left = k;
    while (left >= 0 && state.collapsed[left]) left--;
    let right = k + 1;
    while (right < n && state.collapsed[right]) right++;
    const canDrag = left >= 0 && right < n;
    // during a drag every flexible pane's weight equals its pixel size, so px deltas apply uniformly
    const base: Track[] = state.tracks.map((t, i) => (t.unit === "fr" ? { unit: "fr", size: Math.max(1, startPx[i]) } : t));
    moved.current = false;
    if (canDrag) {
      setDragging(k);
      document.body.classList.add(horizontal ? "rs-resizing-x" : "rs-resizing-y");
    }

    let latest: Track[] | null = null;
    const collapsedNow = state.collapsed;
    const onMove = (ev: PointerEvent) => {
      let delta = (horizontal ? ev.clientX : ev.clientY) - startPos;
      if (Math.abs(delta) > 3) moved.current = true;
      if (!moved.current || !canDrag) return;
      const minL = minOf(left);
      const minR = minOf(right);
      if (startPx[left] + delta < minL) delta = minL - startPx[left];
      if (startPx[right] - delta < minR) delta = startPx[right] - minR;
      latest = base.map((t, i) => {
        if (i === left) return { ...t, size: startPx[left] + delta };
        if (i === right) return { ...t, size: startPx[right] - delta };
        return t;
      });
      setState((s) => ({ ...s, tracks: latest! }));
    };
    const onUp = () => {
      target.removeEventListener("pointermove", onMove);
      target.removeEventListener("pointerup", onUp);
      target.removeEventListener("pointercancel", onUp);
      document.body.classList.remove("rs-resizing-x", "rs-resizing-y");
      setDragging(null);
      if (latest) save(id, { tracks: latest, collapsed: collapsedNow });
      if (!moved.current && toggleIdx != null) toggle(toggleIdx);
    };
    target.addEventListener("pointermove", onMove);
    target.addEventListener("pointerup", onUp);
    target.addEventListener("pointercancel", onUp);
  };

  const reset = () => {
    // a double-click on a chevron is two toggles, not a reset
    if (pressOnToggle.current) return;
    commit({ tracks: defaults, collapsed: new Array(n).fill(false) });
  };

  // the collapse / expand buttons a gutter offers, for the panes on either side of it
  const actionsOf = (k: number): GutterAction[] => {
    if (state.collapsed[k]) return [{ target: k, expand: true }];
    if (state.collapsed[k + 1]) return [{ target: k + 1, expand: true }];
    const out: GutterAction[] = [];
    if (isCollapsible(k)) out.push({ target: k, expand: false });
    if (isCollapsible(k + 1)) out.push({ target: k + 1, expand: false });
    return out;
  };

  const toggle = (i: number) => {
    const collapsed = state.collapsed.slice();
    collapsed[i] = !collapsed[i];
    // never let every pane collapse
    if (collapsed.every(Boolean)) return;
    commit({ ...state, collapsed });
  };

  const chevron = (k: number, a: GutterAction) => {
    const towardsStart = a.target === k; // pane before the gutter
    const shrink = !a.expand;
    if (horizontal) return towardsStart === shrink ? "‹" : "›";
    return towardsStart === shrink ? "⌃" : "⌄";
  };

  const gridStyle: CSSProperties = horizontal ? { gridTemplateColumns: template } : { gridTemplateRows: template };

  return (
    <div ref={ref} className={`split split-${direction}${className ? ` ${className}` : ""}`} style={{ ...gridStyle, ...style }}>
      {kids.map((child, i) => {
        const actions = i < n - 1 ? actionsOf(i) : [];
        return (
          <SplitCellAndGutter
            key={i}
            i={i}
            n={n}
            child={child}
            collapsed={state.collapsed[i]}
            horizontal={horizontal}
            dragging={dragging === i}
            actions={actions.map((a) => ({ ...a, glyph: chevron(i, a) }))}
            onDown={onGutterDown(i)}
            onReset={reset}
            onToggle={toggle}
          />
        );
      })}
    </div>
  );
}

interface GutterAction {
  target: number;
  expand: boolean;
}

/**
 * Thin drag handle for a single fixed edge (sidebar width, dock height). `invert` flips the
 * drag direction, for a handle on the far side of the element it sizes (e.g. the top of a dock).
 */
export function EdgeResizer({
  axis,
  value,
  onChange,
  onReset,
  invert = false,
  className,
  title = "Drag to resize · double-click to reset",
}: {
  axis: "x" | "y";
  value: number;
  onChange: (px: number) => void;
  onReset?: () => void;
  invert?: boolean;
  className?: string;
  title?: string;
}) {
  const [dragging, setDragging] = useState(false);
  const onDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);
    const start = axis === "x" ? e.clientX : e.clientY;
    const startValue = value;
    setDragging(true);
    document.body.classList.add(axis === "x" ? "rs-resizing-x" : "rs-resizing-y");
    const onMove = (ev: PointerEvent) => {
      const delta = (axis === "x" ? ev.clientX : ev.clientY) - start;
      onChange(startValue + (invert ? -delta : delta));
    };
    const onUp = () => {
      target.removeEventListener("pointermove", onMove);
      target.removeEventListener("pointerup", onUp);
      target.removeEventListener("pointercancel", onUp);
      document.body.classList.remove("rs-resizing-x", "rs-resizing-y");
      setDragging(false);
    };
    target.addEventListener("pointermove", onMove);
    target.addEventListener("pointerup", onUp);
    target.addEventListener("pointercancel", onUp);
  };
  return (
    <div
      className={`${className ?? ""}${dragging ? " dragging" : ""}`}
      role="separator"
      aria-orientation={axis === "x" ? "vertical" : "horizontal"}
      onPointerDown={onDown}
      onDoubleClick={onReset}
      title={title}
    />
  );
}

function SplitCellAndGutter({
  i,
  n,
  child,
  collapsed,
  horizontal,
  dragging,
  actions,
  onDown,
  onReset,
  onToggle,
}: {
  i: number;
  n: number;
  child: ReactNode;
  collapsed: boolean;
  horizontal: boolean;
  dragging: boolean;
  actions: (GutterAction & { glyph: string })[];
  onDown: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onReset: () => void;
  onToggle: (i: number) => void;
}) {
  const hasCollapsed = actions.some((a) => a.expand);
  return (
    <>
      <div className={`split-cell${collapsed ? " collapsed" : ""}`} aria-hidden={collapsed || undefined}>
        {child}
      </div>
      {i < n - 1 ? (
        <div
          className={`split-gutter${dragging ? " dragging" : ""}${hasCollapsed ? " has-collapsed" : ""}`}
          role="separator"
          aria-orientation={horizontal ? "vertical" : "horizontal"}
          onPointerDown={onDown}
          onDoubleClick={onReset}
          title="Drag to resize · double-click to reset"
        >
          {actions.length ? (
            <div className="split-toggles">
              {actions.map((a) => (
                <button
                  key={a.target}
                  type="button"
                  className="split-toggle"
                  data-target={a.target}
                  onClick={(e) => {
                    // pointer presses are handled by the gutter; only keyboard activation lands here
                    if (e.detail === 0) onToggle(a.target);
                  }}
                  title={a.expand ? "Expand pane" : "Collapse pane"}
                  aria-label={a.expand ? "Expand pane" : "Collapse pane"}
                >
                  {a.glyph}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
